import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  FileText, Plus, Loader2, Eye, Trash2, Check, X, Clock,
  CheckCircle, XCircle, Package, Calendar, Search, Users, AlertTriangle
} from "lucide-react";

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", icon: Clock },
  { value: "aprobada", label: "Aprobada", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle },
  { value: "rechazada", label: "Rechazada", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300", icon: XCircle }
];

const StatsCards = ({ solicitudes, activeFilter, onFilterChange }) => {
  const stats = [
    { key: "all", label: "Total", count: solicitudes.length, icon: Package, color: "blue" },
    { key: "pendiente", label: "Pendientes", count: solicitudes.filter(s => s.estado === "pendiente").length, icon: Clock, color: "amber" },
    { key: "aprobada", label: "Aprobadas", count: solicitudes.filter(s => s.estado === "aprobada").length, icon: CheckCircle, color: "green" },
    { key: "rechazada", label: "Rechazadas", count: solicitudes.filter(s => s.estado === "rechazada").length, icon: XCircle, color: "red" },
  ];

  const colorClasses = {
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400",
    green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {stats.map(stat => {
        const Icon = stat.icon;
        const isActive = activeFilter === stat.key;
        return (
          <Card
            key={stat.key}
            className={`cursor-pointer transition-all hover:shadow-md dark:bg-slate-800 dark:border-slate-700 ${isActive ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => onFilterChange(stat.key)}
            data-testid={`stat-${stat.key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses[stat.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.count}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default function PermisosGHPage() {
  const { id: solicitudIdParam } = useParams();
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [tiposPermiso, setTiposPermiso] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("solicitudes");
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [formData, setFormData] = useState({
    tipo_permiso: "",
    fecha_inicio: "",
    fecha_fin: "",
    dias_solicitados: 0,
    horas_solicitadas: 0,
    hora_inicio: "",
    hora_fin: "",
    motivo: "",
    requiere_compensacion: false
  });
  
  // Selection state for bulk delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "gestion_humana"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [solRes, tiposRes] = await Promise.all([
        axios.get(`${API}/permisos/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/permisos/tipos`, getAuthHeaders())
      ]);
      setSolicitudes(solRes.data);
      setTiposPermiso(tiposRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Si viene un ID en la URL, abrir el diálogo de detalle
  useEffect(() => {
    if (solicitudIdParam && solicitudes.length > 0) {
      const solicitud = solicitudes.find(s => s.id === solicitudIdParam);
      if (solicitud) {
        setSelectedSolicitud(solicitud);
        setShowViewDialog(true);
      } else {
        toast.error("Solicitud no encontrada");
        navigate("/gestion-humana/permisos");
      }
    }
  }, [solicitudIdParam, solicitudes, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipo_permiso || !formData.fecha_inicio) {
      toast.error("Complete los campos requeridos");
      return;
    }
    
    // Validar campos de horas obligatorios
    if (!formData.hora_inicio || !formData.hora_fin) {
      toast.error("Los campos de hora inicio y hora fin son obligatorios");
      return;
    }
    
    // Calcular horas solicitadas
    const [startH, startM] = formData.hora_inicio.split(':').map(Number);
    const [endH, endM] = formData.hora_fin.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const horasCalc = (endMinutes - startMinutes) / 60;
    
    if (horasCalc <= 0) {
      toast.error("La hora de fin debe ser mayor a la hora de inicio");
      return;
    }
    
    // Validar máximo 4 horas
    if (horasCalc > 4) {
      toast.error("El permiso es máximo 4 horas. Si necesita más tiempo, debe solicitar un día de vacaciones.");
      return;
    }
    
    const dataToSend = {
      ...formData,
      horas_solicitadas: horasCalc,
      fecha_fin: formData.fecha_inicio // Para permisos por horas, fecha_fin = fecha_inicio
    };
    
    setSaving(true);
    try {
      await axios.post(`${API}/permisos/solicitudes`, dataToSend, getAuthHeaders());
      toast.success("Solicitud creada exitosamente");
      setShowDialog(false);
      setFormData({
        tipo_permiso: "",
        fecha_inicio: "",
        fecha_fin: "",
        dias_solicitados: 0,
        horas_solicitadas: 0,
        hora_inicio: "",
        hora_fin: "",
        motivo: "",
        requiere_compensacion: false
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear solicitud");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (solicitud, approve) => {
    try {
      await axios.put(
        `${API}/permisos/solicitudes/${solicitud.id}/estado?estado=${approve ? 'aprobada' : 'rechazada'}`,
        {},
        getAuthHeaders()
      );
      toast.success(approve ? "Solicitud aprobada" : "Solicitud rechazada");
      fetchData();
      setShowViewDialog(false);
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar esta solicitud?")) return;
    try {
      await axios.delete(`${API}/permisos/solicitudes/${id}`, getAuthHeaders());
      toast.success("Solicitud eliminada");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const filteredSolicitudes = solicitudes.filter(sol => {
    const matchesFilter = statusFilter === "all" || sol.estado === statusFilter;
    const matchesSearch = !searchQuery || 
      sol.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sol.motivo?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Selection functions for bulk delete
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredSolicitudes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSolicitudes.map(s => s.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!deletePassword || selectedIds.length === 0) {
      toast.error("Ingrese su contraseña para confirmar");
      return;
    }
    
    setDeleting(true);
    try {
      const res = await axios.post(
        `${API}/permisos/solicitudes/bulk-delete`,
        {
          ids: selectedIds,
          password: deletePassword
        },
        getAuthHeaders()
      );
      
      toast.success(`${res.data.deleted || selectedIds.length} solicitudes eliminadas`);
      setSelectedIds([]);
      setShowBulkDeleteDialog(false);
      setDeletePassword("");
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al eliminar solicitudes";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const getTipoNombre = (tipoId) => {
    const tipo = tiposPermiso.find(t => t.id === tipoId);
    return tipo?.nombre || tipoId;
  };

  const getTipoColor = (tipoId) => {
    const tipo = tiposPermiso.find(t => t.id === tipoId);
    return tipo?.color || "#6b7280";
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                Permisos
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestión de solicitudes de permisos
              </p>
            </div>
            <Button onClick={() => setShowDialog(true)} className="gap-2" data-testid="new-permiso-btn">
              <Plus className="w-4 h-4" />
              Nueva Solicitud
            </Button>
          </div>

          {/* Stats Cards */}
          <StatsCards 
            solicitudes={solicitudes} 
            activeFilter={statusFilter} 
            onFilterChange={setStatusFilter} 
          />

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-white dark:bg-slate-800 border dark:border-slate-700">
              <TabsTrigger value="solicitudes">Solicitudes</TabsTrigger>
              <TabsTrigger value="calendario">Calendario</TabsTrigger>
              {isAdmin && <TabsTrigger value="saldos">Saldos</TabsTrigger>}
            </TabsList>

            <TabsContent value="solicitudes">
              {/* Search and Bulk Actions */}
              <div className="mb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o motivo..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 dark:bg-slate-800 dark:border-slate-700"
                  />
                </div>
                {isAdmin && filteredSolicitudes.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all-perm"
                      checked={selectedIds.length === filteredSolicitudes.length && filteredSolicitudes.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                    <Label htmlFor="select-all-perm" className="text-sm cursor-pointer">
                      {selectedIds.length === filteredSolicitudes.length ? "Deseleccionar" : "Seleccionar"} todo
                    </Label>
                    {selectedIds.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowBulkDeleteDialog(true)}
                        data-testid="bulk-delete-perm-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar ({selectedIds.length})
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Table */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                  ) : filteredSolicitudes.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      No hay solicitudes
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-900">
                          <tr>
                            {isAdmin && <th className="w-12 p-4"></th>}
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Solicitante</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Tipo</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Fecha</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Horas</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Estado</th>
                            <th className="text-right p-4 font-medium text-slate-600 dark:text-slate-300">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {filteredSolicitudes.map(sol => {
                            const estado = ESTADOS.find(e => e.value === sol.estado);
                            const isOwnRequest = sol.user_id === user?.id;
                            const isSelected = selectedIds.includes(sol.id);
                            return (
                              <tr key={sol.id} className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                                {isAdmin && (
                                  <td className="p-4">
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleSelect(sol.id)}
                                    />
                                  </td>
                                )}
                                <td className="p-4">
                                  <div className="flex items-center gap-2">
                                    <div>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-medium text-slate-900 dark:text-white">{sol.user_name}</p>
                                        {!isOwnRequest && (
                                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                                            <Users className="w-3 h-3 mr-1" />
                                            Supervisado{sol.created_by_name && sol.created_by_name !== sol.user_name ? `: ${sol.created_by_name}` : ''}
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-500">{sol.user_departamento}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4">
                                  <Badge style={{ backgroundColor: getTipoColor(sol.tipo_permiso), color: 'white' }}>
                                    {getTipoNombre(sol.tipo_permiso)}
                                  </Badge>
                                </td>
                                <td className="p-4 text-slate-600 dark:text-slate-300">
                                  {sol.fecha_inicio}
                                  {sol.hora_inicio && sol.hora_fin && (
                                    <span className="block text-xs text-slate-500">
                                      {sol.hora_inicio} - {sol.hora_fin}
                                    </span>
                                  )}
                                </td>
                                <td className="p-4 text-slate-900 dark:text-white font-medium">
                                  {sol.horas_solicitadas ? `${sol.horas_solicitadas}h` : `${sol.dias_solicitados}d`}
                                </td>
                                <td className="p-4">
                                  <Badge className={estado?.color}>
                                    {estado?.label}
                                  </Badge>
                                </td>
                                <td className="p-4">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => { setSelectedSolicitud(sol); setShowViewDialog(true); }}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    {sol.estado === "pendiente" && (
                                      <>
                                        {isAdmin && (
                                          <>
                                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleApprove(sol, true)}>
                                              <Check className="w-4 h-4" />
                                            </Button>
                                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleApprove(sol, false)}>
                                              <X className="w-4 h-4" />
                                            </Button>
                                          </>
                                        )}
                                        {sol.user_id === user?.id && (
                                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(sol.id)}>
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="calendario">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Calendario de Permisos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-500 dark:text-slate-400">
                    Vista de calendario próximamente
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {isAdmin && (
              <TabsContent value="saldos">
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Saldos de Permisos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-500 dark:text-slate-400">
                      Gestión de saldos próximamente
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* New Request Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Nueva Solicitud de Permiso</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Tipo de Permiso *</Label>
                <Select value={formData.tipo_permiso} onValueChange={(v) => setFormData({...formData, tipo_permiso: v})}>
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposPermiso.map(tipo => (
                      <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Fecha del Permiso *</Label>
                <Input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => {
                    setFormData({
                      ...formData, 
                      fecha_inicio: e.target.value,
                      fecha_fin: e.target.value
                    });
                  }}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hora Inicio *</Label>
                  <Input
                    type="time"
                    value={formData.hora_inicio}
                    onChange={(e) => setFormData({...formData, hora_inicio: e.target.value})}
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div>
                  <Label>Hora Fin *</Label>
                  <Input
                    type="time"
                    value={formData.hora_fin}
                    onChange={(e) => setFormData({...formData, hora_fin: e.target.value})}
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
              </div>

              {/* Mostrar horas calculadas */}
              {formData.hora_inicio && formData.hora_fin && (() => {
                const [startH, startM] = formData.hora_inicio.split(':').map(Number);
                const [endH, endM] = formData.hora_fin.split(':').map(Number);
                const startMinutes = startH * 60 + startM;
                const endMinutes = endH * 60 + endM;
                const horasCalc = (endMinutes - startMinutes) / 60;
                const isValid = horasCalc > 0 && horasCalc <= 4;
                
                return (
                  <div className={`p-3 rounded-lg ${isValid ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                    <p className={`text-sm font-medium ${isValid ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                      {horasCalc > 0 ? (
                        <>
                          Horas solicitadas: <strong>{horasCalc.toFixed(1)} horas</strong>
                          {horasCalc > 4 && (
                            <span className="block text-xs mt-1">
                              ⚠️ El permiso es máximo 4 horas. Para más tiempo, solicite vacaciones.
                            </span>
                          )}
                        </>
                      ) : (
                        "La hora de fin debe ser mayor a la hora de inicio"
                      )}
                    </p>
                  </div>
                );
              })()}

              <div>
                <Label>Motivo *</Label>
                <Textarea
                  value={formData.motivo}
                  onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                  placeholder="Describa el motivo del permiso..."
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requiere_compensacion}
                  onCheckedChange={(v) => setFormData({...formData, requiere_compensacion: v})}
                />
                <Label>Requiere compensación de tiempo</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Crear Solicitud
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={(open) => {
          setShowViewDialog(open);
          if (!open && solicitudIdParam) {
            navigate("/gestion-humana/permisos");
          }
        }}>
          <DialogContent className="max-w-lg dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalle de Solicitud</DialogTitle>
            </DialogHeader>
            {selectedSolicitud && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Solicitante</p>
                    <p className="font-medium dark:text-white">{selectedSolicitud.user_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Departamento</p>
                    <p className="font-medium dark:text-white">{selectedSolicitud.user_departamento || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Tipo</p>
                    <Badge style={{ backgroundColor: getTipoColor(selectedSolicitud.tipo_permiso), color: 'white' }}>
                      {getTipoNombre(selectedSolicitud.tipo_permiso)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Estado</p>
                    <Badge className={ESTADOS.find(e => e.value === selectedSolicitud.estado)?.color}>
                      {ESTADOS.find(e => e.value === selectedSolicitud.estado)?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Fecha</p>
                    <p className="font-medium dark:text-white">{selectedSolicitud.fecha_inicio}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Horario</p>
                    <p className="font-medium dark:text-white">
                      {selectedSolicitud.hora_inicio && selectedSolicitud.hora_fin 
                        ? `${selectedSolicitud.hora_inicio} - ${selectedSolicitud.hora_fin}` 
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Horas Solicitadas</p>
                    <p className="font-medium dark:text-white">
                      {selectedSolicitud.horas_solicitadas 
                        ? `${selectedSolicitud.horas_solicitadas} horas` 
                        : `${selectedSolicitud.dias_solicitados} días`}
                    </p>
                  </div>
                </div>
                {selectedSolicitud.motivo && (
                  <div>
                    <p className="text-sm text-slate-500">Motivo</p>
                    <p className="dark:text-white">{selectedSolicitud.motivo}</p>
                  </div>
                )}
                {selectedSolicitud.estado === "pendiente" && isAdmin && (
                  <div className="flex gap-2 pt-4 border-t dark:border-slate-700">
                    <Button onClick={() => handleApprove(selectedSolicitud, true)} className="flex-1 bg-green-600 hover:bg-green-700">
                      <Check className="w-4 h-4 mr-2" />
                      Aprobar
                    </Button>
                    <Button onClick={() => handleApprove(selectedSolicitud, false)} variant="destructive" className="flex-1">
                      <X className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Confirmar Eliminación Masiva
              </DialogTitle>
              <DialogDescription>
                Está a punto de eliminar <strong>{selectedIds.length}</strong> solicitudes de permisos de forma permanente.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      Esta acción es IRREVERSIBLE
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Las solicitudes seleccionadas serán eliminadas permanentemente.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-password-perm">Ingrese su contraseña para confirmar</Label>
                <Input
                  id="delete-password-perm"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && handleBulkDelete()}
                  data-testid="delete-password-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowBulkDeleteDialog(false);
                  setDeletePassword("");
                }}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={deleting || !deletePassword}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedIds.length} solicitudes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
